// src/components/Experiment/PlateGrid.js
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Dropdown } from 'react-bootstrap';

const PlateGrid = ({
  rows,
  cols,
  samples,
  selectedCells,
  highlightedSample,
  onCellSelection,
  onSelectionStart,
  onSelectionEnd,
  // New props for context menu
  selectedClassGroup,
  classes,
  onSampleClassAssignment
}) => {
  const canvasRef = useRef(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

  // Context menu and multi-selection state
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    samples: []
  });

  // Track CTRL key state
  const [ctrlPressed, setCtrlPressed] = useState(false);

  // Track selected samples for multi-assignment
  const [selectedSamples, setSelectedSamples] = useState([]);

  // Handle CTRL key events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setCtrlPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setCtrlPressed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Calculate cell positions and sizes
  const getCellLayout = useCallback(() => {
    const padding = 40;
    const availableWidth = canvasSize.width - 2 * padding;
    const availableHeight = canvasSize.height - 2 * padding;

    const cellSize = Math.min(
      availableWidth / cols,
      availableHeight / rows,
      60 // Maximum cell size
    );

    const gridWidth = cellSize * cols;
    const gridHeight = cellSize * rows;

    const startX = (canvasSize.width - gridWidth) / 2;
    const startY = (canvasSize.height - gridHeight) / 2;

    return {
      cellSize,
      startX,
      startY,
      gridWidth,
      gridHeight
    };
  }, [rows, cols, canvasSize]);

  // Get cell at coordinates
  const getCellAt = useCallback((x, y) => {
    const layout = getCellLayout();

    if (x < layout.startX || y < layout.startY ||
        x > layout.startX + layout.gridWidth ||
        y > layout.startY + layout.gridHeight) {
      return null;
    }

    const col = Math.floor((x - layout.startX) / layout.cellSize);
    const row = Math.floor((y - layout.startY) / layout.cellSize);

    if (row >= 0 && row < rows && col >= 0 && col < cols) {
      return { row, col };
    }

    return null;
  }, [rows, cols, getCellLayout]);

  // Get sample at cell
  const getSampleAt = useCallback((row, col) => {
    return samples.find(sample =>
      sample.cells.some(cell => cell.row === row && cell.col === col)
    );
  }, [samples]);

  // Get cells in selection rectangle
  const getCellsInRect = useCallback((start, end) => {
    const cells = [];
    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);

    for (let row = minRow; row <= maxRow; row++) {
      for (let col = minCol; col <= maxCol; col++) {
        cells.push({ row, col });
      }
    }

    return cells;
  }, []);

  // Draw the grid - FIXED: Use selectedSamples instead of contextMenu.samples for highlighting
  const drawGrid = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const layout = getCellLayout();

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create sample lookup
    const sampleLookup = {};
    samples.forEach(sample => {
      sample.cells.forEach(cell => {
        const key = `${cell.row}-${cell.col}`;
        sampleLookup[key] = sample;
      });
    });

    // Draw cells
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const x = layout.startX + col * layout.cellSize;
        const y = layout.startY + row * layout.cellSize;
        const radius = layout.cellSize * 0.35;
        const centerX = x + layout.cellSize / 2;
        const centerY = y + layout.cellSize / 2;

        const cellKey = `${row}-${col}`;
        const sample = sampleLookup[cellKey];
        const isSelected = selectedCells.some(cell => cell.row === row && cell.col === col);
        const isHighlighted = highlightedSample && sample && sample.id === highlightedSample;
        const isMultiSelected = selectedSamples.some(s => s.id === sample?.id); // FIXED: Use selectedSamples
        const isContextMenuSample = contextMenu.samples.some(s => s.id === sample?.id);

        // Draw cell background
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);

        if (isMultiSelected) {
          // Highlight for multi-selected samples
          ctx.fillStyle = '#28a745';
          ctx.strokeStyle = '#1e7e34';
          ctx.lineWidth = 3;
        } else if (isContextMenuSample) {
          // Special highlighting for context menu sample
          ctx.fillStyle = '#ff6b35';
          ctx.strokeStyle = '#e55a2b';
          ctx.lineWidth = 3;
        } else if (isHighlighted) {
          ctx.fillStyle = '#ffc107';
          ctx.strokeStyle = '#ff6b35';
          ctx.lineWidth = 3;
        } else if (isSelected) {
          ctx.fillStyle = '#007bff';
          ctx.strokeStyle = '#0056b3';
          ctx.lineWidth = 2;
        } else if (sample) {
          // Check if sample has class in selected group
          let fillColor = `hsl(${sample.hue}, 60%, 80%)`;
          let strokeColor = `hsl(${sample.hue}, 60%, 60%)`;

          if (selectedClassGroup && sample.classes && sample.classes[selectedClassGroup]) {
            const classId = sample.classes[selectedClassGroup];
            const classItem = classes[selectedClassGroup]?.find(cls => cls.id === classId);
            if (classItem) {
              fillColor = classItem.color;
              strokeColor = classItem.color;
              // Make stroke darker
              const hsl = classItem.color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
              if (hsl) {
                strokeColor = `hsl(${hsl[1]}, ${hsl[2]}%, ${Math.max(0, parseInt(hsl[3]) - 20)}%)`;
              }
            }
          }

          ctx.fillStyle = fillColor;
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = 2;
        } else {
          ctx.fillStyle = '#f8f9fa';
          ctx.strokeStyle = '#dee2e6';
          ctx.lineWidth = 1;
        }

        ctx.fill();
        ctx.stroke();

        // Draw cell label
        const label = String.fromCharCode(65 + row) + (col + 1);
        ctx.fillStyle = sample ? '#333' : '#6c757d';
        ctx.font = `${Math.max(10, layout.cellSize * 0.2)}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, centerX, centerY);

        // Draw multi-selection indicator
        if (isMultiSelected) {
          ctx.beginPath();
          ctx.arc(centerX, centerY, radius * 0.7, 0, 2 * Math.PI);
          ctx.fillStyle = 'rgba(40, 167, 69, 0.3)';
          ctx.fill();
        }
      }
    }

    // Draw row and column headers
    ctx.fillStyle = '#6c757d';
    ctx.font = `${Math.max(12, layout.cellSize * 0.25)}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Column headers
    for (let col = 0; col < cols; col++) {
      const x = layout.startX + col * layout.cellSize + layout.cellSize / 2;
      const y = layout.startY - 20;
      ctx.fillText((col + 1).toString(), x, y);
    }

    // Row headers
    ctx.textAlign = 'right';
    for (let row = 0; row < rows; row++) {
      const x = layout.startX - 10;
      const y = layout.startY + row * layout.cellSize + layout.cellSize / 2;
      ctx.fillText(String.fromCharCode(65 + row), x, y);
    }

    // Draw selection rectangle
    if (isSelecting && selectionStart && selectionEnd) {
      const startLayout = getCellLayout();
      const startX = startLayout.startX + selectionStart.col * startLayout.cellSize;
      const startY = startLayout.startY + selectionStart.row * startLayout.cellSize;
      const endX = startLayout.startX + (selectionEnd.col + 1) * startLayout.cellSize;
      const endY = startLayout.startY + (selectionEnd.row + 1) * startLayout.cellSize;

      ctx.strokeStyle = '#007bff';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(
        Math.min(startX, endX),
        Math.min(startY, endY),
        Math.abs(endX - startX),
        Math.abs(endY - startY)
      );
      ctx.setLineDash([]);
    }

    // Draw CTRL indicator - MOVED to bottom right corner
    if (ctrlPressed) {
      ctx.fillStyle = '#6c757d';
      ctx.font = '14px Arial';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';

      // Calculate position in bottom right corner with padding
      const padding = 20;
      const textX = canvas.width - padding;
      const textY = canvas.height - padding;

      // Draw background for better readability
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillRect(textX - 180, textY - 50, 170, 45);

      // Draw text
      ctx.fillStyle = '#495057';
      ctx.font = 'bold 14px Arial';
      ctx.fillText('CTRL: Multi-select mode', textX, textY - 30);

      // Draw selection count
      if (selectedSamples.length > 0) {
        ctx.fillStyle = '#28a745';
        ctx.font = '13px Arial';
        ctx.fillText(`${selectedSamples.length} sample(s) selected`, textX, textY - 10);
      } else {
        ctx.fillStyle = '#6c757d';
        ctx.font = '13px Arial';
        ctx.fillText('Right-click to select samples', textX, textY - 10);
      }

      // Draw a small CTRL key icon
      ctx.fillStyle = '#6c757d';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('⎈', textX - 160, textY - 25);
    }
  }, [rows, cols, samples, selectedCells, highlightedSample, isSelecting, selectionStart, selectionEnd, getCellLayout, selectedClassGroup, classes, selectedSamples, contextMenu.samples, ctrlPressed]);// FIXED: Add selectedSamples to dependencies

  // Handle mouse down - ONLY for left clicks
  const handleMouseDown = (e) => {
    // Only handle left mouse button (button === 0)
    if (e.button !== 0) return;

    // Close context menu if open
    if (contextMenu.show) {
      setContextMenu({ show: false, x: 0, y: 0, samples: [] });
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const cell = getCellAt(x, y);
    if (cell) {
      setIsSelecting(true);
      setSelectionStart(cell);
      setSelectionEnd(cell);
      onSelectionStart();
    }
  };

  const handleMouseMove = (e) => {
    if (!isSelecting || !selectionStart) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const cell = getCellAt(x, y);
    if (cell) {
      setSelectionEnd(cell);
    }
  };

  const handleMouseUp = (e) => {
    // Only handle left mouse button release
    if (e.button !== 0) return;

    if (isSelecting && selectionStart && selectionEnd) {
      const cells = getCellsInRect(selectionStart, selectionEnd);
      onCellSelection(cells);
    }

    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    onSelectionEnd();
  };

  // Handle right-click context menu - FIXED: Handle multi-select logic better
  const handleContextMenu = (e) => {
    e.preventDefault();

    // Get the cell that was right-clicked
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const cell = getCellAt(x, y);
    if (!cell) return;

    const sample = getSampleAt(cell.row, cell.col);
    if (!sample || !selectedClassGroup) return;

    // If CTRL is pressed, toggle selection without showing menu
    if (ctrlPressed) {
      // Toggle sample in selection
      setSelectedSamples(prev => {
        const exists = prev.find(s => s.id === sample.id);
        if (exists) {
          // Remove from selection
          return prev.filter(s => s.id !== sample.id);
        } else {
          // Add to selection
          return [...prev, sample];
        }
      });

      // Don't show context menu while CTRL is held
      return;
    }

    // Normal context menu behavior (when CTRL is not pressed)
    // Determine which samples to show in the context menu
    let samplesToAssign;

    if (selectedSamples.length > 0) {
      // If we have selected samples, check if the clicked sample is among them
      const clickedSampleIsSelected = selectedSamples.some(s => s.id === sample.id);

      if (clickedSampleIsSelected) {
        // Use all selected samples
        samplesToAssign = [...selectedSamples];
      } else {
        // Use clicked sample plus selected samples
        samplesToAssign = [sample, ...selectedSamples];
      }
    } else {
      // No selected samples, just use the clicked one
      samplesToAssign = [sample];
    }

    // Show context menu with the determined samples
    setContextMenu({
      show: true,
      x: e.clientX,
      y: e.clientY,
      samples: samplesToAssign
    });

    // Clear selection after showing menu (optional - user might want to keep selecting)
    // setSelectedSamples([]);
  };

  // Handle class assignment from context menu
  const handleAssignClass = (classId) => {
    if (contextMenu.samples.length > 0 && onSampleClassAssignment) {
      const sampleIds = contextMenu.samples.map(s => s.id);
      onSampleClassAssignment(sampleIds, selectedClassGroup, classId);
    }
    setContextMenu({ show: false, x: 0, y: 0, samples: [] });
    // Clear selection after assignment
    setSelectedSamples([]);
  };

  // Handle remove class assignment
  const handleRemoveClassAssignment = () => {
    if (contextMenu.samples.length > 0 && onSampleClassAssignment) {
      const sampleIds = contextMenu.samples.map(s => s.id);
      onSampleClassAssignment(sampleIds, selectedClassGroup, null);
    }
    setContextMenu({ show: false, x: 0, y: 0, samples: [] });
    // Clear selection after removal
    setSelectedSamples([]);
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.show) {
        setContextMenu({ show: false, x: 0, y: 0, samples: [] });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.show]);

  // Clear selected samples when CTRL is released - FIXED: Add better logic
  useEffect(() => {
    if (!ctrlPressed) {
      // Optional: You can choose to keep selection after releasing CTRL
      // or clear it. For now, let's keep it until user makes an assignment
      // setSelectedSamples([]);
    }
  }, [ctrlPressed]);

  // Update canvas size
  useEffect(() => {
    const updateSize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        const containerWidth = container.clientWidth;
        const aspectRatio = Math.max(cols / rows, 0.5);
        const width = Math.min(containerWidth - 20, 1000);
        const height = Math.min(width / aspectRatio, 600);

        setCanvasSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [rows, cols]);

  // Redraw when dependencies change
  useEffect(() => {
    drawGrid();
  }, [drawGrid]);

  // Set canvas size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      drawGrid();
    }
  }, [canvasSize, drawGrid]);

  // Get current class assignment for context menu samples
  const getCurrentClassAssignment = () => {
    if (!contextMenu.samples.length || !selectedClassGroup) return null;

    // Check if all selected samples have the same class assignment
    const firstSampleClassId = contextMenu.samples[0].classes?.[selectedClassGroup];
    const allSameClass = contextMenu.samples.every(
      sample => sample.classes?.[selectedClassGroup] === firstSampleClassId
    );

    if (allSameClass && firstSampleClassId) {
      return classes[selectedClassGroup]?.find(cls => cls.id === firstSampleClassId);
    }
    return null; // Mixed or no assignments
  };

  return (
    <div className="plate-grid-container">
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={(e) => {
          // Handle mouse leave for selection (left button only)
          if (isSelecting && e.buttons === 1) {
            handleMouseUp(e);
          }
        }}
        onContextMenu={handleContextMenu}
        style={{
          cursor: isSelecting ? 'crosshair' : (ctrlPressed ? 'crosshair' : 'default'),
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          display: 'block',
          margin: '0 auto'
        }}
      />

      {/* Context Menu */}
      {contextMenu.show && selectedClassGroup && classes[selectedClassGroup] && (
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 1050,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            minWidth: '220px'
          }}
        >
          <div className="context-menu-header px-3 py-2 border-bottom bg-light">
            <small className="fw-bold">
              {contextMenu.samples.length === 1 ?
                contextMenu.samples[0].name :
                `${contextMenu.samples.length} samples selected`
              }
            </small>
            <br />
            <small className="text-muted">
              Assign to {selectedClassGroup}
            </small>
          </div>

          <div className="context-menu-body">
            {classes[selectedClassGroup].map(classItem => {
              const currentClass = getCurrentClassAssignment();
              const isCurrentClass = currentClass?.id === classItem.id;
              return (
                <div
                  key={classItem.id}
                  className={`context-menu-item px-3 py-2 ${isCurrentClass ? 'bg-primary text-white' : ''}`}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee'
                  }}
                  onClick={() => handleAssignClass(classItem.id)}
                  onMouseEnter={(e) => {
                    if (!isCurrentClass) {
                      e.target.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCurrentClass) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="me-2"
                      style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: classItem.color,
                        borderRadius: '2px'
                      }}
                    ></div>
                    <span>{classItem.name}</span>
                    {isCurrentClass && (
                      <i className="bi bi-check ms-auto"></i>
                    )}
                  </div>
                </div>
              );
            })}

            {getCurrentClassAssignment() && (
              <>
                <div className="border-top"></div>
                <div
                  className="context-menu-item px-3 py-2 text-danger"
                  style={{ cursor: 'pointer' }}
                  onClick={handleRemoveClassAssignment}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Remove Assignment
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlateGrid;